"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import {
  CONSENT_OPEN_EVENT,
  readConsent,
  writeConsent,
} from "@/lib/consent";

type View = "hidden" | "banner" | "settings";

export default function CookieConsent() {
  const [view, setView] = useState<View>("hidden");
  const [analytics, setAnalytics] = useState(false);

  useEffect(() => {
    const existing = readConsent();
    if (!existing) {
      setView("banner");
    } else {
      setAnalytics(existing.analytics);
    }

    function handleOpen() {
      const current = readConsent();
      setAnalytics(current?.analytics ?? false);
      setView("settings");
    }
    window.addEventListener(CONSENT_OPEN_EVENT, handleOpen);
    return () => window.removeEventListener(CONSENT_OPEN_EVENT, handleOpen);
  }, []);

  function acceptAll() {
    writeConsent({ analytics: true });
    setAnalytics(true);
    setView("hidden");
  }

  function rejectAll() {
    writeConsent({ analytics: false });
    setAnalytics(false);
    setView("hidden");
  }

  function saveSelection() {
    writeConsent({ analytics });
    setView("hidden");
  }

  if (view === "hidden") return null;

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-label="Slapukų pasirinkimas"
      className="fixed inset-x-0 bottom-0 z-50 px-4 pb-4 sm:px-6 sm:pb-6"
    >
      <div className="mx-auto max-w-3xl rounded-2xl bg-white shadow-2xl border border-gray-200 p-5 sm:p-6">
        {view === "banner" && (
          <>
            <h2 className="text-base font-semibold text-gray-900 mb-1">
              Slapukai šioje svetainėje
            </h2>
            <p className="text-sm text-gray-600 leading-relaxed">
              Naudojame būtinuosius slapukus, kad svetainė veiktų. Su Jūsų sutikimu taip
              pat naudosime analitinius slapukus, padedančius tobulinti svetainę. Sutikimą
              galite bet kada pakeisti per nuorodą puslapio apačioje. Daugiau —{" "}
              <Link href="/slapukai" className="text-blue-600 underline hover:text-blue-700">
                slapukų politika
              </Link>
              .
            </p>
            <div className="mt-4 flex flex-col sm:flex-row gap-2 sm:justify-end">
              <button
                type="button"
                onClick={() => setView("settings")}
                className="px-4 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Nustatymai
              </button>
              <button
                type="button"
                onClick={rejectAll}
                className="px-4 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Atsisakau
              </button>
              <button
                type="button"
                onClick={acceptAll}
                className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-colors"
              >
                Sutinku su visais
              </button>
            </div>
          </>
        )}

        {view === "settings" && (
          <>
            <h2 className="text-base font-semibold text-gray-900 mb-3">
              Slapukų nustatymai
            </h2>

            <div className="space-y-3">
              <div className="flex items-start justify-between gap-4 p-3 rounded-lg border border-gray-200">
                <div>
                  <div className="text-sm font-medium text-gray-900">Būtinieji</div>
                  <p className="text-xs text-gray-600 mt-0.5">
                    Reikalingi rezervacijos formai ir saugumui. Be jų svetainė neveikia.
                  </p>
                </div>
                <span className="text-xs font-medium text-gray-500 whitespace-nowrap pt-0.5">
                  Visada įjungti
                </span>
              </div>

              <label className="flex items-start justify-between gap-4 p-3 rounded-lg border border-gray-200 cursor-pointer">
                <div>
                  <div className="text-sm font-medium text-gray-900">Analitiniai</div>
                  <p className="text-xs text-gray-600 mt-0.5">
                    Anoniminė statistika, kad suprastume kaip naudojama svetainė.
                  </p>
                </div>
                <input
                  type="checkbox"
                  checked={analytics}
                  onChange={(e) => setAnalytics(e.target.checked)}
                  className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
              </label>
            </div>

            <div className="mt-4 flex flex-col sm:flex-row gap-2 sm:justify-end">
              <button
                type="button"
                onClick={rejectAll}
                className="px-4 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Atsisakau visų
              </button>
              <button
                type="button"
                onClick={saveSelection}
                className="px-4 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Išsaugoti pasirinkimą
              </button>
              <button
                type="button"
                onClick={acceptAll}
                className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-colors"
              >
                Sutinku su visais
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
